import { useFormContext } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { IconButton } from '@calendly/ui/components/icon-button';
import { AlertWarningTriangleIcon, EditPencilIcon } from '@calendly/ui/components/icons';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useRef, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useGetUserQuery } from 'common';
import { Layout } from 'components/layout';
import { isOnlyGroupAdminSelector } from 'selectors/current_user';
import { useSelectSoloGroupHost } from './use_select_solo_group_host';
import { useSelectTeamGroupHost } from './use_select_team_group_host';
import { PanelContext } from '../../../../../components/accordion/panel';
import { filterEnabledMemberships } from '../../../helpers';
import { isSameMembership } from '../../helpers';
import { useUpdateFormikMembershipsOnCreate } from '../../hooks';
import { HostPicker } from '../host_picker';
import { HostListItem } from '../round_robin';
import { ListWrapper } from '../styles';
const Container = props => <Layout direction="column" gap="8" {...props} />;
const ListItemWrapper = props => <Layout direction="column" {...props} />;
const ownerNoticeCss = "o16z37gi";
const OwnerNotice = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'p';
  return <Component ref={ref} {...restProps} className={classNames(ownerNoticeCss, props.className)} />;
});
const dropdownContainerWithPlaceholderCss = "d7e4elf";
const dropdownContainerCss = "drxs6ay";
const DropdownContainer = forwardRef((props, ref) => {
  const {
    withPlaceholder,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(dropdownContainerCss, {
    [dropdownContainerWithPlaceholderCss]: withPlaceholder
  }, props.className)} />;
});
const contextualBannerButtonContainerCss = "ccoit2r";
const ContextualBannerButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(contextualBannerButtonContainerCss, props.className)} />;
});
export const SingleHostSelect = ({
  eventType
}) => {
  const initialRender = useRef(true);
  const isOnlyGroupAdmin = useSelector(isOnlyGroupAdminSelector);
  const {
    data: currentUser
  } = useGetUserQuery();
  const {
    values: {
      memberships
    },
    isSubmitting
  } = useFormContext();
  const {
    firstTimeExpanded
  } = useContext(PanelContext);
  const {
    isTeam,
    isGroup
  } = eventType;
  const isTeamGroup = isTeam && isGroup;
  const selectedMembership = useMemo(() => isTeamGroup ? filterEnabledMemberships(memberships)[0] : memberships[0], [isTeamGroup, memberships]);
  const [toggleDropdown, Dropdown, isDropdownOpen] = useDropdown(!selectedMembership);
  const isCurrentUserSelected = selectedMembership?.user_id === currentUser.id;
  const showOwnerNotice = !isTeamGroup && !isCurrentUserSelected;
  const handleTeamGroupHostSelect = useSelectTeamGroupHost(eventType, selectedMembership, toggleDropdown);
  const handleSoloGroupHostSelect = useSelectSoloGroupHost(selectedMembership, toggleDropdown);
  const handleSelect = isTeamGroup ? handleTeamGroupHostSelect : handleSoloGroupHostSelect;
  const prepareHostOptionProps = useCallback(membership => isSameMembership(selectedMembership, membership) ? {
    selected: true,
    disabled: false
  } : {
    selected: false,
    disabled: false
  }, [selectedMembership]);
  useEffect(() => {
    if (initialRender.current && firstTimeExpanded) {
      initialRender.current = false;
      if (!isDropdownOpen) {
        setTimeout(() => toggleDropdown(), 300);
      }
    }
  }, [toggleDropdown, firstTimeExpanded, initialRender, isDropdownOpen]);
  useUpdateFormikMembershipsOnCreate(eventType);
  const editButton = <IconButton size="small" disabled={isSubmitting} onClick={() => toggleDropdown()} title={I18n.t('publisher.action.change')}>
      <EditPencilIcon />
    </IconButton>;
  return <Container>
      {isDropdownOpen ? <DropdownContainer withPlaceholder={isDropdownOpen}>
          <Dropdown>
            <HostPicker isDisabled={isSubmitting} memberships={memberships} onSelect={handleSelect} placeholder={I18n.t('publisher.event_type_editor.sections.hosts.picker.placeholder.search_host')} prepareHostOptionProps={prepareHostOptionProps} searchForGroupAdmin={!isTeam && isOnlyGroupAdmin} searchMembershipsOnly={!isTeam} skipSearch={isTeam} />
          </Dropdown>
        </DropdownContainer> : <>
          {selectedMembership ? <ListWrapper>
              <ListItemWrapper>
                <HostListItem member={selectedMembership} removeActionComponent={editButton} />
              </ListItemWrapper>
            </ListWrapper> : <ContextualBanner variant="danger" icon={AlertWarningTriangleIcon}>
              <Layout justify="space-between">
                {I18n.t('publisher.event_type_editor.sections.hosts_and_invitees.errors.no_hosts')}
                <ContextualBannerButtonContainer>
                  {editButton}
                </ContextualBannerButtonContainer>
              </Layout>
            </ContextualBanner>}
          {showOwnerNotice && <OwnerNotice>{I18n.t('publisher.event_types.select_host_modal.note')}</OwnerNotice>}
        </>}
    </Container>;
};

require("./single_host_select.wyw-in-js.css!=!../../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./single_host_select.js");